import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/backend/client";
import { UnderMaintenancePage } from "@/components/public/UnderMaintenancePage";
import { Product } from "@/types/store";
import { Profile, PageSettings } from "@/types/editor";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Loader2, ShoppingBag, Eye, Share2, Search, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { ProductImageCarousel } from "@/components/ui/ProductImageCarousel";
import { ProductDetailSheet, getEffectiveLinks } from "@/components/ui/ProductDetailSheet";
import { PaymentLink } from "@/types/store";
import { getBackgroundStyle, getPresetAnimationClass } from "@/lib/background";
import { getButtonClass, getGlobalButtonStyle } from "@/lib/linkStyle";
import { SeoHead } from "@/components/seo/SeoHead";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { ChatWidget } from '@/components/chat/ChatWidget';
import { getBioTheme, setBioTheme, getInstantBg } from '@/lib/bioThemeCache';

export default function PublicStorePage() {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { username } = useParams<{ username: string }>();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  
  const [profile, setProfile] = useState<Profile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<PageSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [viewSource, setViewSource] = useState<'public' | 'preview' | 'owner'>('public');
  const [storeChatAgentId, setStoreChatAgentId] = useState<string | null>(null);
  const [profileAvatarError, setProfileAvatarError] = useState(false);
  const [detailProduct, setDetailProduct] = useState<typeof products[0] | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedCount, setLoadedCount] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Store display config from settings
  const displayMode = settings?.store_display_mode || 'pagination';
  const perPage = settings?.store_products_per_page || 12;
  const searchEnabled = settings?.store_search_enabled !== false;

  // Filter products by search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = searchQuery.trim().toLowerCase();
    return products.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.description && p.description.toLowerCase().includes(q))
    );
  }, [products, searchQuery]);

  // Paginated / infinite-scroll slice
  const visibleProducts = useMemo(() => {
    if (displayMode === 'infinite_scroll') {
      return filteredProducts.slice(0, loadedCount);
    }
    // pagination
    const start = (currentPage - 1) * perPage;
    return filteredProducts.slice(start, start + perPage);
  }, [filteredProducts, displayMode, currentPage, perPage, loadedCount]);

  const totalPages = Math.ceil(filteredProducts.length / perPage);
  const hasMore = displayMode === 'infinite_scroll' && loadedCount < filteredProducts.length;

  // Initialise loaded count for infinite scroll
  useEffect(() => {
    if (displayMode === 'infinite_scroll') {
      setLoadedCount(perPage);
    }
  }, [displayMode, perPage]);

  // Reset to page 1 when search changes or display config changes
  useEffect(() => {
    setCurrentPage(1);
    if (displayMode === 'infinite_scroll') {
      setLoadedCount(perPage);
    }
  }, [searchQuery, displayMode, perPage]);

  // Infinite scroll observer
  const loadMore = useCallback(() => {
    setLoadedCount((prev) => Math.min(prev + perPage, filteredProducts.length));
  }, [perPage, filteredProducts.length]);

  useEffect(() => {
    if (displayMode !== 'infinite_scroll' || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [displayMode, loadMore]);

  useEffect(() => {
    async function fetchData() {
      if (!username) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        let uid: string | null = null;
        let profileData = null;
        let detectedViewSource: 'public' | 'preview' | 'owner' = 'public';

        // Check if accessing via preview token
        if (previewToken) {
          const { data: tokenUserId } = await supabase.rpc('validate_preview_token', {
            _token: previewToken
          });

          if (tokenUserId) {
            uid = tokenUserId as string;
            
            const { data: tokenProfile } = await supabase
              .from("profiles")
              .select("*")
              .eq("user_id", uid)
              .eq("username", username)
              .maybeSingle();

            if (tokenProfile) {
              profileData = tokenProfile;
              setIsPreview(true);
              detectedViewSource = 'preview';
            }
          }
        }

        // Get user ID if not set
        if (!uid) {
          const { data: userIdData } = await supabase.rpc("get_user_id_by_username", { _username: username });
          uid = userIdData as string | null;
        }

        if (!uid) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setUserId(uid);
        setViewSource(detectedViewSource);

        // Check if premium
        const { data: subData } = await supabase.from("user_plans").select("plan_id").eq("user_id", uid).maybeSingle();

        const plan = subData?.plan_id;
        if (!plan || plan === "free" || plan === "starter") {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setIsPremium(true);

        // Fetch profile if not already loaded via token
        if (!profileData) {
          const { data: publicData } = await supabase
            .from("profiles_public")
            .select("*")
            .eq("username", username)
            .maybeSingle();
          
          profileData = publicData;

          // If not found in public view, check if current user is the owner
          if (!profileData) {
            const { data: { user } } = await supabase.auth.getUser();
            
            if (user && user.id === uid) {
              const { data: ownerProfile } = await supabase
                .from("profiles")
                .select("*")
                .eq("username", username)
                .eq("user_id", uid)
                .maybeSingle();
              
              if (ownerProfile) {
                profileData = ownerProfile;
                setIsPreview(true);
                detectedViewSource = 'owner';
                setViewSource('owner');
              }
            }
          }
        }

        if (profileData) {
          setProfile({
            id: profileData.id,
            user_id: uid,
            username: profileData.username,
            full_name: profileData.full_name ?? null,
            display_name: profileData.display_name,
            bio: profileData.bio,
            avatar_url: profileData.avatar_url,
            is_published: profileData.is_published ?? true,
            created_at: profileData.created_at,
            updated_at: profileData.updated_at || profileData.created_at,
          } as Profile);
          // If the profile exists but page is not published and visitor is not
          // the owner (preview mode), show the maintenance screen.
          // Use strict false check: profiles_public view does not expose is_published,
          // so undefined must not be treated as unpublished.
          if (profileData.is_published === false && detectedViewSource !== 'owner' && detectedViewSource !== 'preview') {
            setIsUnderMaintenance(true);
            setLoading(false);
            return;
          }
        } else {
          // Profile not found in public view — maintenance check
          const { data: statusRows } = await (supabase.rpc as any)('get_page_status', { p_username: username });
          const status = statusRows?.[0];
          if (status?.page_exists && !status.is_published) {
            setIsUnderMaintenance(true);
          } else {
            setNotFound(true);
          }
          setLoading(false);
          return;
        }

        // Fetch products
        const { data: productsData } = await supabase
          .from("products")
          .select("*")
          .eq("user_id", uid)
          .eq("is_active", true)
          .order("position", { ascending: true });

        setProducts((productsData as Product[]) || []);

        // Fetch page settings for styling
        const { data: settingsData }: any = await supabase
          .from("page_settings")
          .select("*")
          .eq("user_id", uid)
          .maybeSingle();

        if (settingsData) setSettings(settingsData as unknown as PageSettings);

        // Guard: if the owner has disabled the store, treat as not found
        // (bypass for owner and shared-preview views so the owner can still audit their page)
        if (
          settingsData &&
          settingsData.store_link_enabled === false &&
          detectedViewSource !== 'owner' &&
          detectedViewSource !== 'preview'
        ) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Cache theme for instant loading-state backgrounds on navigation
        if (settingsData && profileData) {
          setBioTheme(username!, {
            settings: settingsData as unknown as PageSettings,
            profile: {
              username: username!,
              display_name: profileData.display_name ?? null,
              bio: profileData.bio ?? null,
              avatar_url: profileData.avatar_url ?? null,
            },
          });
        }

        // Chat widget for business plan visitors
        if (plan === 'business' && uid) {
          const { data: agentData }: any = await supabase
            .from('chat_agents')
            .select('id')
            .eq('user_id', uid)
            .eq('is_enabled', true)
            .maybeSingle();
          if (agentData) setStoreChatAgentId(agentData.id);
        }
        // Track visit only for public viewers with an active store
        if (
          plan && plan !== 'free' &&
          detectedViewSource === 'public' &&
          settingsData?.store_link_enabled !== false
        ) {
          await supabase.from("store_visits").insert({
            user_id: uid,
            view_source: detectedViewSource,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent || null,
          });
        }
      } catch (error) {
        console.error("Error fetching store:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [username]);

  const trackProductClick = async (productId: string) => {
    if (!userId) return;
    await supabase.from("product_clicks").insert({
      product_id: productId,
      user_id: userId,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
    });
  };

  if (loading) {
    const cached = getBioTheme(username ?? '');
    const bgStyle = cached ? getInstantBg(cached.settings) : {};
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.12 } }}
        transition={{ duration: 0.22, ease: 'easeOut', delay: 0.15 }}
        className="min-h-screen flex items-center justify-center"
        style={cached ? bgStyle : { background: 'hsl(var(--background))' }}
      >
        <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" style={cached ? { color: cached.settings.text_color } : {}} />
      </motion.div>
    );
  }

  if (isUnderMaintenance) {
    return <UnderMaintenancePage username={username} />;
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8">
        <h1 className="text-2xl font-bold text-foreground mb-4">{t("Loja não encontrada", "Store not found")}</h1>
        <p className="text-muted-foreground mb-8">{t("Esta loja não existe ou não está disponível.", "This store doesn't exist or isn't available.")}</p>
        <Link to="/" className="text-primary hover:underline">
          {t("Crie sua bio page →", "Create your own bio page →")}
        </Link>
      </div>
    );
  }

  const storeTitle = settings?.store_name
    ? `${settings.store_name} | Qlivar`
    : t(`Loja de ${profile?.display_name || `@${username}`} | Qlivar`, `${profile?.display_name || `@${username}`}'s Store | Qlivar`);
  const storeDescription = profile?.display_name
    ? t(`Compre produtos e ofertas de ${profile.display_name} no Qlivar.`, `Shop products and offers from ${profile.display_name} on Qlivar.`)
    : t(`Veja os produtos disponíveis na loja de @${username}.`, `View available products in @${username}'s store.`);

  // Theme-aware card surface — light text → dark bg theme → use dark glass card so contrast holds.
  // Dark text → light bg theme → near-white card.
  const textColorIsLight = (() => {
    const hex = (settings?.text_color ?? '#1a1a2e').replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
  })();
  const cardBg = textColorIsLight ? 'rgba(20,20,28,0.78)' : 'rgba(255,255,255,0.96)';
  const cardBorder = textColorIsLight ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';
  const cardText = settings?.text_color || (textColorIsLight ? '#ffffff' : '#1a1a2e');
  const cardSubtext = textColorIsLight ? 'rgba(255,255,255,0.65)' : 'rgba(26,26,46,0.62)';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.12 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="min-h-screen"
      style={{
        background: settings ? getBackgroundStyle(settings) : "#ffffff",
        fontFamily: settings?.font_family || "Inter",
      }}
    >
      <SeoHead
        title={storeTitle}
        description={storeDescription}
        path={`/${username}/store`}
        noindex={isPreview || !isPremium}
      />
      {settings?.font_family && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${settings.font_family.replace(" ", "+")}:wght@400;500;600;700&display=swap`}
          rel="stylesheet"
        />
      )}

      {/* Preview Mode Banner */}
      {isPreview && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
          {viewSource === 'preview' ? (
            <>
              <Share2 className="inline-block h-4 w-4" />
              {t("Prévia compartilhada - este é um link temporário", "Shared Preview - This is a temporary preview link")}
            </>
          ) : (
            <>
              <Eye className="inline-block h-4 w-4" />
              {t("Modo de prévia - esta página ainda não está publicada. Só você pode vê-la.", "Preview Mode - This page is not published yet. Only you can see it.")}
            </>
          )}
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(4rem,calc(env(safe-area-inset-bottom)+1.5rem))]">
        {/* Profile header strip */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2.5 mb-8">
          <Link
            to={previewToken ? `/${username}?preview=${encodeURIComponent(previewToken)}` : `/${username}`}
            title={t("Voltar para bio", "Back to bio")}
            aria-label={t("Voltar para bio", "Back to bio")}
            className="h-11 w-11 sm:h-9 sm:w-9 -ml-1 sm:ml-0 rounded-full flex items-center justify-center shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            style={{ color: settings?.text_color || '#1a1a2e' }}
          >
            <ArrowLeft className="h-5 w-5 sm:h-4 sm:w-4" />
          </Link>
          {profile?.avatar_url && !profileAvatarError ? (
            <img src={profile.avatar_url} alt={profile.display_name ?? ''} className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-black/5" onError={() => setProfileAvatarError(true)} />
          ) : (
            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(0,0,0,0.10)', color: settings?.text_color || '#1a1a2e' }}>
              <ShoppingBag className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight truncate tracking-tight" style={{ color: settings?.text_color || '#1a1a2e' }}>
              {profile?.display_name || `@${username}`}
            </p>
            {profile?.bio && (
              <p className="text-xs opacity-60 truncate leading-snug" style={{ color: settings?.text_color || '#1a1a2e' }}>
                {profile.bio}
              </p>
            )}
          </div>
        </motion.div>

        {/* Store name — shown only when explicitly configured */}
        {settings?.store_name && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2.5 mb-8">
            <ShoppingBag className="h-5 w-5 opacity-70" style={{ color: settings?.text_color || '#1a1a2e' }} />
            <h1 className="text-[1.5rem] sm:text-[1.65rem] font-bold tracking-tight leading-tight" style={{ color: settings?.text_color || '#1a1a2e' }} data-no-runtime-i18n>
              {settings.store_name}
            </h1>
          </motion.div>
        )}

        {/* Search bar */}
        {searchEnabled && products.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                style={{ color: settings?.text_color || '#1a1a2e', opacity: 0.7 }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("Buscar produtos...", "Search products...")}
                className="w-full h-11 pl-10 pr-4 rounded-xl text-sm outline-none transition-colors duration-200 ease-out placeholder:opacity-60 focus:ring-2"
                style={{
                  color: settings?.text_color || '#1a1a2e',
                  border: `1.5px solid ${settings?.text_color || '#1a1a2e'}35`,
                  backgroundColor: `${settings?.text_color || '#1a1a2e'}12`,
                  boxShadow: 'none',
                  '--tw-ring-color': `${settings?.text_color || '#1a1a2e'}40`,
                } as React.CSSProperties}
                data-no-runtime-i18n
              />
            </div>
          </motion.div>
        )}

        {/* Products grid */}
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p style={{ color: settings?.text_color || "#1a1a2e" }} className="opacity-70">
              {t("Nenhum produto disponível no momento.", "No products available yet.")}
            </p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p style={{ color: settings?.text_color || "#1a1a2e" }} className="opacity-70">
              {t("Nenhum produto encontrado.", "No products found.")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index, 8) * 0.05, duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                  className="group rounded-2xl overflow-hidden shadow-lg transition-[box-shadow,border-color] duration-200 hover:shadow-xl"
                  style={{
                    backgroundColor: cardBg,
                    border: `1px solid ${cardBorder}`,
                    backdropFilter: textColorIsLight ? 'blur(14px)' : undefined,
                    WebkitBackdropFilter: textColorIsLight ? 'blur(14px)' : undefined,
                  }}
                >
                  {product.image_url || (product.images && product.images.length > 0) ? (
                    <div className="overflow-hidden">
                      <ProductImageCarousel
                        images={product.images && product.images.length > 0 ? product.images : [product.image_url!]}
                        alt={product.name}
                        className="w-full h-44 object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                        placeholderClassName="w-full h-44 bg-muted flex items-center justify-center"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-44 flex items-center justify-center" style={{ background: textColorIsLight ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 opacity-30" style={{ color: cardText }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-[0.98rem] tracking-tight leading-snug mb-1" style={{ color: cardText }}>{product.name}</h3>
                    {product.description && (
                      <p className="text-sm mb-3 line-clamp-2 leading-relaxed" style={{ color: cardSubtext }}>{product.description}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[1.05rem] font-bold tracking-tight" style={{ color: cardText }}>
                        {product.currency} {Number(product.price).toFixed(2)}
                      </span>
                      {(() => {
                        const links = getEffectiveLinks(product);
                        if (links.length === 0) {
                          return <span className="text-xs uppercase tracking-wide font-medium" style={{ color: cardSubtext }}>{t("Em breve", "Coming soon")}</span>;
                        }
                        if (links.length === 1) {
                          return (
                            <a
                              href={links[0].url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackProductClick(product.id)}
                              className={`inline-flex items-center gap-1 px-4 py-2.5 sm:py-2 text-sm font-medium min-h-[2.5rem] sm:min-h-0 transition-[background-color,box-shadow,opacity] duration-150 hover:opacity-90 ${settings ? getButtonClass(settings) : 'rounded-xl'}`}
                              style={settings ? getGlobalButtonStyle(settings) : {}}
                            >
                              {links[0].label || settings?.store_button_text || t("Comprar agora", "Buy Now")}
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          );
                        }
                        return (
                          <button
                            onClick={() => { trackProductClick(product.id); setDetailProduct(product); }}
                            className={`inline-flex items-center gap-1 px-4 py-2.5 sm:py-2 text-sm font-medium min-h-[2.5rem] sm:min-h-0 transition-[background-color,box-shadow,opacity] duration-150 hover:opacity-90 ${settings ? getButtonClass(settings) : 'rounded-xl'}`}
                            style={settings ? getGlobalButtonStyle(settings) : {}}
                          >
                            {t("Ver opções", "See options")}
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination controls */}
            {displayMode === 'pagination' && totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-[background-color,opacity] disabled:opacity-30 disabled:pointer-events-none hover:bg-white/10"
                  style={{ color: settings?.text_color || '#1a1a2e' }}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t("Anterior", "Previous")}
                </button>
                <span className="text-sm font-medium opacity-60" style={{ color: settings?.text_color || '#1a1a2e' }}>
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-[background-color,opacity] disabled:opacity-30 disabled:pointer-events-none hover:bg-white/10"
                  style={{ color: settings?.text_color || '#1a1a2e' }}
                >
                  {t("Próxima", "Next")}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Infinite scroll sentinel */}
            {displayMode === 'infinite_scroll' && (
              <div ref={sentinelRef} className="flex justify-center py-8">
                {hasMore && <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none opacity-50" style={{ color: settings?.text_color || '#1a1a2e' }} />}
              </div>
            )}
          </>
        )}

        {/* Discrete footer */}
        <motion.div
          className="mt-14 pb-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Link
            to="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-wide uppercase opacity-40 hover:opacity-80 transition-opacity"
            style={{ color: settings?.text_color || "#1a1a2e" }}
          >
            <span>{t("Feito com", "Made with")}</span>
            <img alt="Qlivar" className="h-3 w-3" src="/uploads/c60b833a-df7a-4878-bcf0-647598b01880.svg" />
            <span className="font-semibold">Qlivar</span>
          </Link>
        </motion.div>

      </div>
      {/* Floating chat widget */}
      {storeChatAgentId && settings && (
        <ChatWidget agentId={storeChatAgentId} settings={settings} />
      )}

      {/* Product detail sheet (multi-link) */}
      <ProductDetailSheet
        product={detailProduct}
        settings={settings}
        open={!!detailProduct}
        onClose={() => setDetailProduct(null)}
        onBuyClick={(p, _l) => { trackProductClick(p.id); }}
        textColor={settings?.text_color}
      />
    </motion.div>
  );
}
